import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import BackButton from "../components/BackButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  Beaker, FlaskConical, MessageSquare, FileText, Database, LogOut,
  Plus, Trash2, Download, Upload, Loader2, Brain, Sparkles, Target,
  CheckCircle, AlertCircle, History, FileUp, Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = [
  { value: "formulation", label: "Formulation" },
  { value: "material_properties", label: "Material Properties" },
  { value: "processing", label: "Processing" },
  { value: "troubleshooting", label: "Troubleshooting" },
  { value: "applications", label: "Applications" },
  { value: "testing", label: "Testing Methods" },
  { value: "general", label: "General Q&A" },
];

export default function TrainingDataPage() {
  const { user, logout } = useAuth();
  const [examples, setExamples] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadName, setUploadName] = useState("");
  const [chatSessions, setChatSessions] = useState([]);
  const [formulationItems, setFormulationItems] = useState([]);
  const [tab, setTab] = useState("examples");
  const [form, setForm] = useState({ prompt: "", completion: "", category: "formulation", tags: "" });

  useEffect(() => { fetch(); }, []);
  const fetch = async () => {
    try {
      const [e, s, c, f] = await Promise.all([
        axios.get(`${API}/training-data`),
        axios.get(`${API}/training-data/stats`),
        axios.get(`${API}/chat-history`),
        axios.get(`${API}/formulation-history`).catch(() => ({ data: [] })),
      ]);
      setExamples(e.data); setStats(s.data); setChatSessions(c.data); setFormulationItems(f.data || []);
    } catch (err) {} finally { setLoading(false); }
  };

  const add = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await axios.post(`${API}/training-data`, { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      setForm({ prompt: "", completion: "", category: "formulation", tags: "" });
      setDialogOpen(false); fetch();
    } catch (err) {} finally { setSubmitting(false); }
  };

  const del = async (id) => {
    try { await axios.delete(`${API}/training-data/${id}`); setExamples((e) => e.filter((x) => x.id !== id)); fetch(); } catch (e) {}
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/training-data/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `elastoai_training_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Failed to export PDF");
    } finally { setExporting(false); }
  };

  const convertChat = async (sid) => {
    try { const r = await axios.post(`${API}/training-data/generate-from-chat/${sid}`); alert(`Created ${r.data.count} examples!`); fetch(); }
    catch (e) {}
  };

  const convertFormulation = async (fid) => {
    try { const r = await axios.post(`${API}/training-data/generate-from-formulation/${fid}`); alert(`Created ${r.data.count} examples!`); fetch(); }
    catch (e) { alert("Conversion failed"); }
  };

  const uploadTrain = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingFile(true);
    setUploadPct(0);
    setUploadName(f.name);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await axios.post(`${API}/training-data/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      alert(`Indexed ${r.data.chunks_indexed} chunks from ${r.data.filename} into your knowledge base.`);
      fetch();
    } catch (err) { alert("Upload failed: " + (err.response?.data?.detail || err.message)); }
    finally { setUploadingFile(false); setUploadPct(0); setUploadName(""); e.target.value = ""; }
  };

  const navItems = [
    { icon: MessageSquare, label: "Chat", path: "/chat" },
    { icon: FlaskConical, label: "Formulation", path: "/formulation" },
    { icon: FileText, label: "Documents", path: "/documents" },
    { icon: Database, label: "Knowledge", path: "/knowledge" },
    { icon: Brain, label: "Training", path: "/training" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  const pct = stats ? Math.min((stats.total_examples / stats.recommended_for_fine_tuning) * 100, 100) : 0;

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${item.path === "/training" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
              <item.icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center"><span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0).toUpperCase()}</span></div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p></div>
          <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg"><LogOut className="w-4 h-4 text-slate-500" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <BackButton />
            <Brain className="w-5 h-5 text-orange-500" />
            <h1 className="font-heading font-semibold text-slate-900">Train with Your Data</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher className="mr-2" />
            <label className="cursor-pointer">
              <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv,.txt" onChange={uploadTrain} data-testid="training-file-upload" disabled={uploadingFile} />
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${uploadingFile ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700 hover:bg-orange-100"}`}>
                {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                {uploadingFile ? (uploadPct < 100 ? `Uploading ${uploadPct}%` : "Indexing into KB...") : "Upload Data File"}
              </span>
            </label>
            <Button variant="outline" onClick={exportPdf} disabled={exporting || !stats?.total_examples} data-testid="export-pdf-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} Export PDF
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-training-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
                  <Plus className="w-4 h-4 mr-1" /> Add Q&A
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader><DialogTitle>Add Training Example</DialogTitle></DialogHeader>
                <form onSubmit={add} className="space-y-3 mt-3">
                  <div><Label>User Prompt</Label><Textarea value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} className="min-h-[100px]" required data-testid="training-prompt-input" /></div>
                  <div><Label>Ideal Completion</Label><Textarea value={form.completion} onChange={(e) => setForm({ ...form, completion: e.target.value })} className="min-h-[150px]" required data-testid="training-completion-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="training-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Tags</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="NBR, oil" data-testid="training-tags-input" /></div>
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="save-training-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Example"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto">
            {stats && (
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Total Examples</span><Target className="w-5 h-5 text-orange-500" /></div>
                  <p className="font-heading text-3xl font-bold text-slate-900">{stats.total_examples}</p>
                  <p className="text-xs text-slate-500 mt-1">Min: {stats.minimum_for_fine_tuning} | Recommended: {stats.recommended_for_fine_tuning}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">RAG Coverage</span>{pct >= 100 ? <CheckCircle className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-amber-500" />}</div>
                  <Progress value={pct} className="h-2 mb-2" />
                  <p className="text-sm text-slate-600">{pct >= 100 ? "Strong RAG coverage" : `${Math.round(pct)}% — keep adding Q&A or upload files`}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Categories</span><Sparkles className="w-5 h-5 text-purple-500" /></div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(stats.by_category || {}).map(([c, n]) => <span key={c} className="px-2 py-0.5 bg-slate-100 rounded-full text-xs text-slate-600">{c}:{n}</span>)}
                    {!Object.keys(stats.by_category || {}).length && <span className="text-xs text-slate-400">No data yet</span>}
                  </div>
                </div>
              </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-white border border-slate-200">
                <TabsTrigger value="examples" data-testid="tab-examples">Q&A Examples</TabsTrigger>
                <TabsTrigger value="import" data-testid="tab-import">Import from Chats</TabsTrigger>
                <TabsTrigger value="import-formulation" data-testid="tab-import-formulation">Import from Formulation</TabsTrigger>
                <TabsTrigger value="guide" data-testid="tab-guide">How It Works</TabsTrigger>
              </TabsList>

              <TabsContent value="examples" className="mt-4">
                {loading ? <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 my-12" /> :
                examples.length === 0 ? (
                  <div className="bg-white rounded-xl border border-slate-200 text-center py-12">
                    <Brain className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-4">No examples yet. Add Q&A or upload a data file to train.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {examples.map((ex) => (
                      <div key={ex.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`training-example-${ex.id}`}>
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1"><span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Q</span><span className="text-xs px-2 py-0.5 bg-slate-100 rounded-full">{ex.category}</span></div>
                            <p className="text-sm text-slate-900 line-clamp-2 mb-2">{ex.prompt}</p>
                            <div className="flex items-center gap-2 mb-1"><span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">A</span></div>
                            <p className="text-sm text-slate-600 line-clamp-3">{ex.completion}</p>
                          </div>
                          <button onClick={() => del(ex.id)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`delete-training-${ex.id}`}>
                            <Trash2 className="w-4 h-4 text-slate-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="import" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4"><History className="w-5 h-5 text-orange-500" /><h3 className="font-heading font-semibold">Convert Chat Sessions</h3></div>
                  <p className="text-slate-600 mb-4 text-sm">Convert past chat sessions into Q&A training pairs.</p>
                  {chatSessions.length === 0 ? <p className="text-slate-400 text-center py-6">No chat sessions</p> :
                    <div className="space-y-2">
                      {chatSessions.slice(0, 15).map((s) => (
                        <div key={s.session_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                          <div><p className="text-sm font-medium text-slate-900">{s.title}</p><p className="text-xs text-slate-500">{s.messages?.length || 0} msgs</p></div>
                          <Button variant="outline" size="sm" onClick={() => convertChat(s.session_id)}><Upload className="w-4 h-4 mr-1" /> Convert</Button>
                        </div>
                      ))}
                    </div>
                  }
                </div>
              </TabsContent>

              <TabsContent value="import-formulation" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <FlaskConical className="w-5 h-5 text-orange-500" />
                    <h3 className="font-heading font-semibold">Convert Formulations & Forward Predictions</h3>
                  </div>
                  <p className="text-slate-600 mb-4 text-sm">
                    Convert your saved Designer formulations and Forward Predictions (including any follow-up Q&amp;A) into Q&amp;A training pairs.
                  </p>
                  {formulationItems.length === 0 ? (
                    <p className="text-slate-400 text-center py-6">No saved formulations yet</p>
                  ) : (
                    <div className="space-y-2">
                      {formulationItems.slice(0, 30).map((f) => (
                        <div key={f.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                          <div className="min-w-0 flex-1 mr-3">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-sm font-medium text-slate-900 truncate">{f.title || (f.type === "forward_prediction" ? "Forward Prediction" : "Formulation")}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${f.type === "forward_prediction" ? "bg-amber-100 text-amber-800" : "bg-orange-100 text-orange-800"}`}>
                                {f.type === "forward_prediction" ? "Forward" : "Designer"}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">{(f.chat_messages || []).length} follow-up msgs · {new Date(f.created_at).toLocaleDateString()}</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => convertFormulation(f.id)} data-testid={`convert-formulation-${f.id}`}>
                            <Upload className="w-4 h-4 mr-1" /> Convert
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="guide" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold">How "Train with Your Data" works</h3>
                  <ol className="list-decimal list-inside text-slate-700 space-y-2 text-sm">
                    <li><b>Upload Data File</b> (PDF, Excel, CSV, TXT) — the file is chunked and stored in your private knowledge base. The chat will retrieve and cite relevant chunks at query time.</li>
                    <li><b>Add Q&A pairs</b> — direct ground-truth that the chat will pull verbatim when a similar question is asked.</li>
                    <li><b>Convert chats</b> — turn good past conversations into training pairs.</li>
                    <li><b>Export JSONL</b> — keep your data portable for future fine-tuning of your own custom model.</li>
                  </ol>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                    Note: This system uses **Retrieval-Augmented Generation (RAG)**, not weight-level fine-tuning. Your data is queried and grounded into every answer — giving you accuracy without GPU training.
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
